import { Link } from "react-router-dom";
import type { GossipEvent } from "@/types/content";
import { formatDate, statusStyleMap } from "@/utils/content";

interface EventCardProps {
  event: GossipEvent;
  variant?: "default" | "featured";
}

export default function EventCard({ event, variant = "default" }: EventCardProps) {
  const badge = statusStyleMap[event.status] ?? "bg-background-100 text-foreground-600 border-background-200";

  if (variant === "featured") {
    return (
      <article className="group rounded-lg overflow-hidden border border-background-200 bg-background-100 h-full flex flex-col lg:flex-row">
        <div className="relative w-full lg:w-[52%] h-56 sm:h-72 lg:h-auto shrink-0 overflow-hidden">
          <img
            src={event.cover}
            alt={event.coverAlt}
            className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
          />
          <span className="absolute top-4 left-4 px-3 py-1 rounded-md bg-background-50/85 backdrop-blur-sm text-xs text-foreground-800 border border-background-200/70">
            {event.category}
          </span>
        </div>
        <div className="p-5 md:p-7 flex flex-col">
          <div className="flex items-center gap-3">
            <span className={`px-2.5 py-1 rounded-md text-xs border ${badge}`}>{event.status}</span>
            <span className="text-xs text-foreground-500">更新于 {formatDate(event.updatedAt)}</span>
          </div>
          <h3 className="mt-4 font-heading text-xl md:text-2xl font-bold text-foreground-950 leading-snug group-hover:text-primary-500 transition-colors">
            <Link to={`/event/${event.id}`} className="cursor-pointer">
              {event.title}
            </Link>
          </h3>
          <p className="mt-3 text-sm leading-7 text-foreground-500 line-clamp-3">{event.summary}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {event.tags.map((tag) => (
              <span key={tag} className="px-2.5 py-1 rounded-full text-xs bg-background-200/70 text-foreground-600">
                #{tag}
              </span>
            ))}
          </div>
          <div className="mt-auto pt-5 flex items-center justify-between border-t border-background-200/70">
            <span className="flex items-center gap-1.5 text-sm text-accent-300">
              <i className="ri-file-list-3-line" />
              {event.evidenceCount} 条证据
            </span>
            <Link
              to={`/event/${event.id}`}
              className="inline-flex items-center gap-1 text-sm text-primary-500 hover:text-primary-400 transition-colors cursor-pointer whitespace-nowrap"
            >
              查看证据合集
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="group rounded-lg overflow-hidden border border-background-200 bg-background-100 flex flex-col h-full transition-colors hover:border-primary-300/60">
      <div className="relative w-full h-44 overflow-hidden shrink-0">
        <img
          src={event.cover}
          alt={event.coverAlt}
          className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-50/80 via-transparent to-transparent" />
        <span className={`absolute bottom-3 left-3 px-2.5 py-1 rounded-md text-xs border ${badge}`}>{event.status}</span>
      </div>
      <div className="p-5 flex flex-col flex-1">
        <span className="text-xs text-primary-500">{event.category}</span>
        <h3 className="mt-2 font-heading text-lg font-bold text-foreground-950 leading-snug group-hover:text-primary-500 transition-colors">
          <Link to={`/event/${event.id}`} className="cursor-pointer">
            {event.title}
          </Link>
        </h3>
        <p className="mt-2 text-sm leading-6 text-foreground-500 line-clamp-2">{event.summary}</p>
        <div className="mt-auto pt-4 flex items-center justify-between text-xs text-foreground-500">
          <span className="flex items-center gap-1.5">
            <i className="ri-file-list-3-line text-accent-400" />
            {event.evidenceCount} 条
          </span>
          <span>{formatDate(event.updatedAt)}</span>
        </div>
      </div>
    </article>
  );
}